import React, { useState } from 'react';
import { Mail } from 'lucide-react';
import * as passwordApi from '../api/password';

/**
 * Ask for a reset link.
 *
 * <strong>There was no way to do this from the portal at all.</strong> The endpoint has
 * always been there; the only route to a reset was an admin generating a link and passing
 * it on by hand, because for as long as the mail path was a stub nothing would have
 * arrived anyway.
 *
 * The confirmation is deliberately the same whether the address is on the platform or
 * not. The server answers the same either way - a different answer for a known address
 * turns this box into a way of asking whether somebody has an account here - and a screen
 * that says "no such account" hands back exactly what the server withheld.
 */
function ForgotPasswordPage({ onSwitchToLogin }) {
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      await passwordApi.requestReset(email);
      setSent(true);
    } catch (err) {
      // Only a genuinely broken request lands here - a missing address, or the server
      // being down. An unknown email is a success as far as this screen is concerned.
      setError(err.name === 'ApiError'
        ? (err.body?.message || 'Could not send that')
        : 'Something went wrong. Try again.');
    } finally {
      setBusy(false);
    }
  };

  if (sent) {
    return (
      <div className="page-container fade-in">
        <div className="auth-card">
          <Mail className="auth-icon" />
          <h1>Check your inbox</h1>
          <p className="auth-subtitle">
            If that address has an account here, a reset link is on its way. It lasts 24
            hours and works once.
          </p>
          <button className="primary-btn full-width" onClick={onSwitchToLogin}>
            Back to log in
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container fade-in">
      <div className="auth-card">
        <Mail className="auth-icon" />
        <h1>Forgotten your password?</h1>
        <p className="auth-subtitle">Put your email in and we will send you a link.</p>

        {error && <div className="error-message">{error}</div>}

        <form onSubmit={submit} className="auth-form">
          <div className="form-group">
            <label htmlFor="reset-email">Email</label>
            <input
              id="reset-email"
              type="email"
              placeholder="The address you signed up with"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={busy}
            />
          </div>

          <button type="submit" className="primary-btn full-width" disabled={busy}>
            {busy ? 'Sending…' : 'Send me a link'}
          </button>

          <p className="auth-switch">
            Remembered it?{' '}
            <button type="button" onClick={onSwitchToLogin}>Log in</button>
          </p>
        </form>
      </div>
    </div>
  );
}

export default ForgotPasswordPage;
